import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Heart, Smile, Meh, Frown, AlertTriangle, TrendingUp, TrendingDown,
  Moon, Battery, Zap, Brain, Coffee, ChevronRight, X, Check, ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOODS = [
  { id: 'great', icon: '😊', label: 'Genial', color: 'bg-green-500' },
  { id: 'good', icon: '🙂', label: 'Bien', color: 'bg-lime-500' },
  { id: 'neutral', icon: '😐', label: 'Normal', color: 'bg-yellow-500' },
  { id: 'tired', icon: '😴', label: 'Cansado', color: 'bg-orange-500' },
  { id: 'stressed', icon: '😰', label: 'Estresado', color: 'bg-red-500' },
  { id: 'anxious', icon: '😟', label: 'Ansioso', color: 'bg-purple-500' }
];

const WellbeingCheckIn = ({ onClose, onComplete }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [checkinData, setCheckinData] = useState({
    energy_level: 3,
    stress_level: 3,
    motivation_level: 3,
    sleep_quality: 3,
    study_satisfaction: 3,
    mood: 'neutral',
    notes: '',
    study_hours_today: 0
  });

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/wellbeing/check-in`, checkinData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      if (onComplete) onComplete(response.data);
      if (onClose) onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar check-in');
    } finally {
      setSubmitting(false);
    }
  };

  const renderSlider = (label, icon, field, lowLabel, highLabel, invert = false) => {
    const Icon = icon;
    const value = checkinData[field];
    const displayValue = invert ? 6 - value : value;
    
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-indigo-500" />
            <span className="font-medium">{label}</span>
          </div>
          <span className="text-sm text-slate-500">{displayValue}/5</span>
        </div>
        <input
          type="range"
          min="1"
          max="5"
          value={value}
          onChange={(e) => setCheckinData({ ...checkinData, [field]: parseInt(e.target.value) })}
          className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
        />
        <div className="flex justify-between text-xs text-slate-400">
          <span>{lowLabel}</span>
          <span>{highLabel}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-800">Check-in de Bienestar</h2>
                <p className="text-sm text-slate-500">¿Cómo te encuentras hoy?</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          
          {/* Progress */}
          <div className="flex gap-2 mt-4">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`flex-1 h-1 rounded-full transition-colors ${
                  s <= step ? 'bg-indigo-500' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {step === 1 && (
            <>
              <h3 className="font-medium text-slate-700">¿Cómo es tu estado de ánimo?</h3>
              <div className="grid grid-cols-3 gap-3">
                {MOODS.map((mood) => (
                  <button
                    key={mood.id}
                    onClick={() => setCheckinData({ ...checkinData, mood: mood.id })}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      checkinData.mood === mood.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <span className="text-3xl block mb-1">{mood.icon}</span>
                    <span className="text-sm text-slate-600">{mood.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {step === 2 && (
            <div className="space-y-6">
              {renderSlider('Nivel de Energía', Battery, 'energy_level', 'Agotado', 'Lleno de energía')}
              {renderSlider('Nivel de Estrés', AlertTriangle, 'stress_level', 'Relajado', 'Muy estresado')}
              {renderSlider('Motivación', Zap, 'motivation_level', 'Desmotivado', 'Muy motivado')}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              {renderSlider('Calidad del Sueño', Moon, 'sleep_quality', 'Muy mal', 'Excelente')}
              {renderSlider('Satisfacción con el Estudio', Brain, 'study_satisfaction', 'Insatisfecho', 'Muy satisfecho')}
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Horas de estudio hoy (aproximado)
                </label>
                <input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  value={checkinData.study_hours_today}
                  onChange={(e) => setCheckinData({ ...checkinData, study_hours_today: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Notas adicionales (opcional)
                </label>
                <textarea
                  value={checkinData.notes}
                  onChange={(e) => setCheckinData({ ...checkinData, notes: e.target.value })}
                  placeholder="¿Algo que quieras compartir?"
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 flex justify-between">
          {step > 1 ? (
            <Button variant="ghost" onClick={() => setStep(step - 1)}>
              Atrás
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
          )}
          
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)}>
              Siguiente
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Guardando...' : 'Completar'}
              <Check className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};


const WellbeingSummary = ({ summary }) => {
  if (!summary) return null;

  const getRiskBadge = (level) => {
    const badges = {
      'healthy': { label: 'Saludable', color: 'bg-green-100 text-green-700' },
      'low_risk': { label: 'Riesgo Bajo', color: 'bg-yellow-100 text-yellow-700' },
      'medium_risk': { label: 'Riesgo Medio', color: 'bg-orange-100 text-orange-700' },
      'high_risk': { label: 'Riesgo Alto', color: 'bg-red-100 text-red-700' },
      'unknown': { label: 'Sin datos', color: 'bg-slate-100 text-slate-700' }
    };
    return badges[level] || badges['unknown'];
  };

  const risk = getRiskBadge(summary.current_risk_level);

  return (
    <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-4 border border-pink-100">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-pink-500" />
          <span className="font-medium text-slate-800">Tu Bienestar</span>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${risk.color}`}>
          {risk.label}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-3">
        <div className="text-center">
          <p className="text-2xl font-bold text-indigo-600">
            {summary.weekly_average?.toFixed(1) || '-'}
          </p>
          <p className="text-xs text-slate-500">Media semanal</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-purple-600">
            {summary.total_checkins_this_week || 0}
          </p>
          <p className="text-xs text-slate-500">Check-ins semana</p>
        </div>
      </div>

      {summary.stress_trend && summary.stress_trend !== 'stable' && (
        <div className={`flex items-center gap-2 text-sm ${
          summary.stress_trend === 'increasing' ? 'text-red-600' : 'text-green-600'
        }`}>
          {summary.stress_trend === 'increasing' ? (
            <TrendingUp className="w-4 h-4" />
          ) : (
            <TrendingDown className="w-4 h-4" />
          )}
          <span>
            Estrés {summary.stress_trend === 'increasing' ? 'aumentando' : 'disminuyendo'}
          </span>
        </div>
      )}
    </div>
  );
};


const WellbeingWidget = () => {
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minimized, setMinimized] = useState(() => {
    return localStorage.getItem('wellbeing_widget_minimized') === 'true';
  });

  const toggleMinimized = () => {
    const newState = !minimized;
    setMinimized(newState);
    localStorage.setItem('wellbeing_widget_minimized', String(newState));
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/wellbeing/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
      
      // Auto-show check-in if needed
      if (response.data.needs_checkin) {
        // Don't auto-show, let user decide
      }
    } catch (error) {
      console.error('Error loading wellbeing summary:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-100 rounded-xl p-4 animate-pulse">
        <div className="h-20 bg-slate-200 rounded"></div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {/* Header with minimize button */}
        <div 
          className={`p-4 flex items-center justify-between ${minimized ? 'cursor-pointer hover:bg-slate-50' : ''}`}
          onClick={minimized ? toggleMinimized : undefined}
        >
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-500" />
            <span className="font-bold text-slate-900">Bienestar</span>
            {minimized && summary?.current_risk_level && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                summary.current_risk_level === 'healthy' ? 'bg-green-100 text-green-700' :
                summary.current_risk_level === 'low_risk' ? 'bg-yellow-100 text-yellow-700' :
                summary.current_risk_level === 'medium_risk' ? 'bg-orange-100 text-orange-700' :
                'bg-red-100 text-red-700'
              }`}>
                {summary.weekly_average?.toFixed(1) || '-'}/5
              </span>
            )}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); toggleMinimized(); }}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            title={minimized ? 'Expandir' : 'Minimizar'}
          >
            {minimized ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
          </button>
        </div>
        
        {/* Content - only show when not minimized */}
        {!minimized && (
          <div className="px-4 pb-4 space-y-3">
            <WellbeingSummary summary={summary} />
            
            {summary?.needs_checkin && (
              <Button
                onClick={() => setShowCheckIn(true)}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
              >
                <Heart className="w-4 h-4 mr-2" />
                Hacer Check-in Diario
              </Button>
            )}
            
            {!summary?.needs_checkin && (
              <p className="text-center text-sm text-slate-500">
                ✓ Check-in de hoy completado
              </p>
            )}
          </div>
        )}
      </div>

      {showCheckIn && (
        <WellbeingCheckIn
          onClose={() => setShowCheckIn(false)}
          onComplete={() => {
            setShowCheckIn(false);
            loadSummary();
          }}
        />
      )}
    </>
  );
};

export { WellbeingCheckIn, WellbeingSummary, WellbeingWidget };
export default WellbeingWidget;
